import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getGrn, getGrnItems } from '../api/grn';
import { getVendor } from '../api/vendor';
import { getUoms } from '../api/inventoryMaster';
import { formatDate, formatQty, lineTotal, computeGrnFinancials } from '../utils';
import { fetchItemCatalog, normalizeItemList, resolveItemDisplayName } from './itemDisplay';
import { resolveFacilityIdentity, formatRegistrationLine } from './facilityIdentity';
import {
  C,
  SIDE_M,
  U,
  CARD_W,
  CARD_X_RIGHT,
  CARD_HEAD_H,
  CARD_PAD_BOT,
  CARD_GAP,
  CONT_TOP,
  DASH,
  Rs,
  pct,
  numberToWords,
  displayOrDash,
  drawLetterhead,
  drawTitleRow,
  measureRows,
  measureLines,
  drawCardFrame,
  drawCardTitle,
  strokeCard,
  drawRows,
  drawLines,
  measureSummary,
  drawSummaryCard,
  measureWordsCard,
  drawWordsCard,
  tableStyles,
  drawRowRule,
  drawItemSubline,
  ensureSpace,
  drawContinuationHeader,
  drawSignatureRow,
  stampFooters,
} from './pdfTheme';

// GRN status vocabulary. In v5 these colour the chip's border and label rather than
// filling it, so they stay legible on a black-and-white printer.
const STATUS_BADGE_COLORS = {
  DRAFT:       [120, 120, 128],
  RECEIVED:    [ 37,  99, 235],
  QC_PENDING:  [180, 120,   0],
  QC_COMPLETE: [ 22, 163,  74],
  POSTED:      [ 15,  76,  92],
  CANCELLED:   [220,  38,  38],
  REJECTED:    [220,  38,  38],
};

function badgeColor(status) { return STATUS_BADGE_COLORS[status] || [120, 120, 128]; }
function badgeLabel(status) { return (status || '').replace(/_/g, ' ') || DASH; }

// Right-aligned twin of drawItemSubline, for annotating a numeric cell. Same
// baseline so it sits on the row's sub-line, not under the figure above it.
function drawCellNote(doc, data, text) {
  const lineCount = data.cell.text?.length || 1;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(...C.faint);
  doc.text(text, data.cell.x + data.cell.width - 1.6, data.cell.y + 1.8 + lineCount * 2.85 + 1.9, { align: 'right' });
}

function resolveGrnItemRate(it) {
  return Number(it.purchase_rate ?? it.unit_rate ?? it.rate) || 0;
}

function resolveGrnItemMrp(it, catalogItem) {
  const n = Number(
    it.mrp ?? it.maximum_retail_price ?? it.sale_rate
    ?? catalogItem?.mrp ?? catalogItem?.default_mrp,
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

function resolveGrnQtyFields(it) {
  const received = Math.floor(Number(it.received_quantity) || 0);
  const accepted = Math.floor(Number(it.accepted_quantity ?? it.accept_quantity ?? received) || 0);
  const rejected = it.rejected_quantity != null
    ? Math.floor(Number(it.rejected_quantity) || 0)
    : Math.max(0, received - accepted);
  // Scheme units accepted at QC. Stocked but never billed, so they stay out of
  // received/accepted and out of every amount on this document.
  const acceptedFree = Math.floor(Number(it.accepted_free_quantity) || 0);
  return { received, accepted, rejected, acceptedFree };
}

function resolveGrnLineAmount(it, accQty, rate, disc, tax) {
  const fromApi = Number(it.line_total ?? it.net_amount ?? it.total_amount);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  return lineTotal(accQty, rate, tax, disc);
}

function normalizeVendor(vendor, extras = {}) {
  const phone = vendor?.contact_phone ?? vendor?.phone ?? null;
  const contact = vendor?.contact_person
    ? [vendor.contact_person, phone].filter(Boolean).join(' - ')
    : phone;
  const address = [
    vendor?.address,
    [vendor?.city, vendor?.state].filter(Boolean).join(', '),
  ].filter(Boolean).join(', ') || null;

  return {
    name: vendor?.vendor_name ?? extras.vendorName ?? null,
    code: vendor?.vendor_code ?? null,
    address,
    gstin: vendor?.gstin ?? vendor?.gst_number ?? null,
    contact,
    email: vendor?.email ?? null,
  };
}

function normalizeGrnPdfData({ grn = {}, items = [], vendor = null, extras = {}, facilityLogo = null }) {
  const supplier = normalizeVendor(vendor, extras);
  const storeName = extras.storeName ?? grn.store_name ?? null;
  const poNumber = extras.poNumber
    ?? grn.po_number
    ?? grn.purchase_order_number
    ?? grn.external_po_reference
    ?? null;

  const document = {
    grnNumber: grn.grn_number ?? null,
    grnDate: formatDate(grn.grn_date || grn.created_at),
    inwardNo: grn.gate_entry_number ?? grn.inward_number ?? grn.inward_no ?? null,
    inwardDate: formatDate(grn.gate_entry_date ?? grn.inward_date ?? grn.received_date),
    poNumber,
    invoiceNo: grn.invoice_number ?? grn.vendor_invoice_number ?? null,
    invoiceDate: formatDate(grn.invoice_date),
    storeName,
    placeOfSupply: grn.place_of_supply ?? grn.state_of_supply ?? null,
    status: grn.posting_status || grn.status || null,
    remarks: grn.qc_remarks || grn.remarks || grn.notes || null,
  };

  const lines = items.map((it) => {
    const { received, accepted, rejected, acceptedFree } = resolveGrnQtyFields(it);
    const rate = resolveGrnItemRate(it);
    const disc = Number(it.discount_percent) || 0;
    const tax = Number(it.tax_percent ?? it.gst_percent) || 0;
    const gross = accepted * rate;
    const discAmt = gross * (disc / 100);
    const basic = gross - discAmt;
    const taxAmt = basic * (tax / 100);
    const amount = resolveGrnLineAmount(it, accepted, rate, disc, tax);

    return {
      ...it,
      received,
      accepted,
      rejected,
      acceptedFree,
      // What a stocked unit actually cost once the free units diluted the
      // payable. Never used for any amount here — those stay on the billed rate.
      effectiveRate: Number(it.effective_unit_cost) || 0,
      rate,
      disc,
      tax,
      mrp: it.mrp,
      gross,
      discAmt,
      basic,
      taxAmt,
      amount,
    };
  });

  const financials = computeGrnFinancials(items, { useAccepted: true });
  let totalGross = 0;
  let totalDiscount = 0;
  let hasDiscount = false;
  const cgstBuckets = {};
  const sgstBuckets = {};
  const igstBuckets = {};

  for (const line of lines) {
    totalGross += line.gross;
    totalDiscount += line.discAmt;
    if (line.disc > 0) hasDiscount = true;

    const isIGST = line.igst_percent > 0 || line.igst_amount > 0;
    if (isIGST) {
      const r = line.igst_percent || line.tax;
      const a = line.igst_amount || line.taxAmt;
      if (r > 0) igstBuckets[r] = (igstBuckets[r] || 0) + a;
    } else if (line.tax > 0) {
      const cr = line.cgst_percent || line.tax / 2;
      const sr = line.sgst_percent || line.tax / 2;
      const ca = line.cgst_amount > 0 ? line.cgst_amount : line.basic * cr / 100;
      const sa = line.sgst_amount > 0 ? line.sgst_amount : line.basic * sr / 100;
      cgstBuckets[cr] = (cgstBuckets[cr] || 0) + ca;
      sgstBuckets[sr] = (sgstBuckets[sr] || 0) + sa;
    }
  }

  const subtotal = totalGross - totalDiscount;
  const receiptTotal = financials.grandTotal > 0 ? financials.grandTotal : subtotal + financials.totalTax;

  return {
    grn,
    supplier,
    document,
    lines,
    storeName,
    totals: {
      subtotal,
      totalTax: financials.totalTax,
      receiptTotal,
      totalGross,
      totalDiscount,
      hasDiscount,
      cgstBuckets,
      sgstBuckets,
      igstBuckets,
    },
    facilityLogo: facilityLogo || extras.facilityLogo || null,
  };
}

function enrichGrnItems(items, catalog, uomMap = {}) {
  return items.map(it => {
    const ci = catalog?.byId?.get(it.item_id || it.id) || null;
    const rate = resolveGrnItemRate(it);
    const mrp = resolveGrnItemMrp(it, ci);
    return {
      ...it,
      item_name:         resolveItemDisplayName(it, catalog),
      item_code:         it.item_code || ci?.item_code || ci?.item_number || ci?.sku || DASH,
      hsn_code:          it.hsn_code  || ci?.hsn_code || DASH,
      uom_display:       it.uom_name  || it.uom || uomMap[it.uom_id] || DASH,
      unit_rate:         rate,
      purchase_rate:     it.purchase_rate ?? it.unit_rate ?? rate,
      mrp,
      manufacturer_name: it.manufacturer_name || it.manufacturer || ci?.manufacturer_name || ci?.manufacturer || null,
      cgst_percent:      Number(it.cgst_percent || it.cgst_rate || 0),
      sgst_percent:      Number(it.sgst_percent || it.sgst_rate || 0),
      igst_percent:      Number(it.igst_percent || it.igst_rate || 0),
      cgst_amount:       Number(it.cgst_amount  || it.cgst_amt  || 0),
      sgst_amount:       Number(it.sgst_amount  || it.sgst_amt  || 0),
      igst_amount:       Number(it.igst_amount  || it.igst_amt  || 0),
    };
  });
}

// ─── Core builder ─────────────────────────────────────────────────────────────
export function createGrnPdfDocument(raw) {
  // `meta`, not `document` — the view-model key of that name would shadow the global
  // for the whole function body.
  const { supplier, document: meta, lines, storeName, totals, facilityLogo } =
    normalizeGrnPdfData(raw);

  // Prints the hospital letterhead unless the caller asked for a blank header, for
  // pre-printed stationery. The layout is identical either way.
  const letterhead = raw?.letterhead ?? true;

  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const identity = resolveFacilityIdentity();
  const registration = formatRegistrationLine(identity);
  const onNewPage = (d) =>
    drawContinuationHeader(d, identity, 'Goods Receipt Note', meta.grnNumber, facilityLogo, letterhead);

  // ── 1. Letterhead + title ──────────────────────────────────────────────────
  let y = drawLetterhead(doc, identity, registration, facilityLogo, letterhead);
  y = drawTitleRow(doc, y, {
    title: 'GOODS RECEIPT NOTE',
    subtitle: 'Original for Store  ·  Goods Inward Document',
    chipLabel: badgeLabel(meta.status),
    chipColor: badgeColor(meta.status),
  });

  // ── 2. GRN INFORMATION + SUPPLIER INFORMATION ──────────────────────────────
  const leftRows = [
    ['GRN No',       meta.grnNumber],
    ['GRN Date',     meta.grnDate],
    ['Inward No',    meta.inwardNo],
    ['Inward Date',  meta.inwardDate],
    ['Against PO',   meta.poNumber],
    ['Invoice No',   meta.invoiceNo],
    ['Invoice Date', meta.invoiceDate],
    ['Store',        meta.storeName],
  ];

  const rightRows = [
    ['Supplier',    supplier.name],
    ['Vendor Code', supplier.code],
    ['Address',     supplier.address],
    ['GSTIN',       supplier.gstin],
    ['Contact',     supplier.contact],
  ];
  if (supplier.email) rightRows.push(['Email', supplier.email]);
  if (meta.placeOfSupply) rightRows.push(['Place of Supply', meta.placeOfSupply]);

  const leftM = measureRows(doc, leftRows, CARD_W);
  const rightM = measureRows(doc, rightRows, CARD_W);
  const infoH = Math.max(leftM.height, rightM.height);

  y = ensureSpace(doc, y, infoH, onNewPage);
  drawRows(doc, SIDE_M, drawCardFrame(doc, SIDE_M, y, CARD_W, infoH, 'GRN Information'),
    CARD_W, leftRows, leftM.counts);
  drawRows(doc, CARD_X_RIGHT, drawCardFrame(doc, CARD_X_RIGHT, y, CARD_W, infoH, 'Supplier Information'),
    CARD_W, rightRows, rightM.counts);
  y += infoH + CARD_GAP;

  // ── 3. RECEIVER ────────────────────────────────────────────────────────────
  const receiverText = [
    [identity.name, identity.address].filter(Boolean).join('  ·  '),
    [storeName, registration].filter(Boolean).join('  ·  '),
  ]
    .filter(Boolean)
    .join('\n');

  const receiverM = measureLines(doc, receiverText, U);
  y = ensureSpace(doc, y, receiverM.height, onNewPage);
  drawLines(
    doc,
    SIDE_M,
    drawCardFrame(doc, SIDE_M, y, U, receiverM.height, 'Receiver'),
    receiverM.lines,
    receiverM.pitch,
  );
  y += receiverM.height + CARD_GAP;

  // ── 4. ITEMS ───────────────────────────────────────────────────────────────
  // Twelve columns is a tight fit in 187mm, so the optional commercial columns are
  // dropped when no line uses them and the width goes back to the description.
  const showMrp = lines.some(l => Number(l.mrp) > 0);
  const showDisc = lines.some(l => Number(l.disc) > 0);

  const columns = [
    { key: 'sl',    label: '#',                w: 6.5,    halign: 'center', bold: true },
    { key: 'desc',  label: 'ITEM DESCRIPTION', w: 'auto' },
    { key: 'uom',   label: 'UOM',              w: 10,     halign: 'center' },
    { key: 'batch', label: 'BATCH / EXP',      w: 19 },
    { key: 'rcvd',  label: 'RCVD',             w: 10,     halign: 'right' },
    { key: 'acc',   label: 'ACCPTD',           w: 13,     halign: 'right' },
    { key: 'rej',   label: 'RJCTD',            w: 11,     halign: 'right' },
    ...(showMrp ? [{ key: 'mrp', label: 'MRP', w: 18, halign: 'right' }] : []),
    { key: 'rate',  label: 'RATE',             w: 18,     halign: 'right' },
    ...(showDisc ? [{ key: 'disc', label: 'DISC%', w: 11, halign: 'right' }] : []),
    { key: 'gst',   label: 'GST%',             w: 10,     halign: 'right' },
    { key: 'amt',   label: 'AMOUNT',           w: 20,     halign: 'right', bold: true },
  ];
  const descIndex = columns.findIndex(c => c.key === 'desc');
  const accIndex = columns.findIndex(c => c.key === 'acc');
  const rateIndex = columns.findIndex(c => c.key === 'rate');

  const tableRows = lines.map((line, i) => {
    const batchLine = [
      line.batch_number,
      line.expiry_date ? `Exp: ${formatDate(line.expiry_date)}` : null,
    ].filter(Boolean).join('  ');

    const cells = {
      sl:    i + 1,
      desc:  line.item_name || DASH,
      uom:   line.uom_display || DASH,
      batch: batchLine || DASH,
      rcvd:  formatQty(line.received),
      acc:   formatQty(line.accepted),
      rej:   line.rejected > 0 ? formatQty(line.rejected) : DASH,
      mrp:   line.mrp ? Rs(line.mrp) : DASH,
      rate:  Rs(line.rate),
      disc:  pct(line.disc),
      gst:   pct(line.tax),
      amt:   Rs(line.amount),
    };
    return columns.map(c => cells[c.key]);
  });

  const emptyRow = columns.map(c => (c.key === 'desc' ? 'No line items found.' : ''));
  const base = tableStyles();

  const TABLE_LEFT = SIDE_M + 1.5;
  const TABLE_RIGHT = SIDE_M + U - 1.5;
  let itemsCardTop = y;

  doc.autoTable({
    startY: y + CARD_HEAD_H,
    head: [columns.map(c => c.label)],
    body: tableRows.length ? tableRows : [emptyRow],
    ...base,
    columnStyles: columns.reduce((acc, c, i) => {
      acc[i] = {
        cellWidth: c.w,
        ...(c.halign ? { halign: c.halign } : {}),
        ...(c.bold ? { fontStyle: 'bold' } : {}),
      };
      return acc;
    }, {}),
    tableWidth: U - 3,
    margin: { top: CONT_TOP + CARD_HEAD_H, left: TABLE_LEFT, right: TABLE_LEFT, bottom: 20 },
    showHead: 'everyPage',
    willDrawPage: (data) => {
      // The document's own page number, not the table-relative one: autotable can
      // push the table to a fresh page without bumping its counter when the head
      // will not fit below the cards.
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) onNewPage(doc);
      itemsCardTop = data.cursor.y - CARD_HEAD_H;
      drawCardTitle(doc, SIDE_M, itemsCardTop, U, 'Items');
    },
    didDrawPage: (data) => {
      strokeCard(doc, SIDE_M, itemsCardTop, U, data.cursor.y + CARD_PAD_BOT - itemsCardTop);
    },
    didDrawCell: (data) => {
      if (data.column.index === 0) drawRowRule(doc, data, TABLE_LEFT, TABLE_RIGHT);
      if (data.section !== 'body') return;
      const line = lines[data.row.index];
      if (!line) return;
      if (data.column.index === descIndex) {
        drawItemSubline(doc, data, [
          line.hsn_code && line.hsn_code !== DASH ? `HSN: ${line.hsn_code}` : null,
          line.manufacturer_name,
        ]);
      }
      // Free units and the cost they dilute to. Annotations rather than columns:
      // the amounts on this document are billed-only and must not look like they
      // were computed on the free units or at the effective rate.
      if (data.column.index === accIndex && line.acceptedFree > 0) {
        drawCellNote(doc, data, `+${formatQty(line.acceptedFree)} free`);
      }
      if (data.column.index === rateIndex && line.acceptedFree > 0 && line.effectiveRate > 0) {
        drawCellNote(doc, data, `eff ${Rs(line.effectiveRate)}`);
      }
    },
  });

  y = doc.lastAutoTable.finalY + CARD_PAD_BOT + CARD_GAP;

  // ── 5. AMOUNT IN WORDS + SUMMARY ───────────────────────────────────────────
  const summaryRows = totals.hasDiscount
    ? [
        ['Sub-Total (Items)', Rs(totals.totalGross)],
        ['Discount (-)',      Rs(totals.totalDiscount)],
        ['Net Taxable',       Rs(totals.subtotal)],
      ]
    : [['Sub-Total', Rs(totals.subtotal)]];

  const byRate = (buckets, label) =>
    Object.entries(buckets)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([r, amt]) => [`${label} (${r}%)`, Rs(amt)]);

  const gstRows = [
    ...byRate(totals.cgstBuckets, 'CGST'),
    ...byRate(totals.sgstBuckets, 'SGST'),
    ...byRate(totals.igstBuckets, 'IGST'),
  ];
  if (gstRows.length) summaryRows.push(...gstRows);
  else if (totals.totalTax > 0) summaryRows.push(['Tax', Rs(totals.totalTax)]);

  const wordsM = measureWordsCard(doc, CARD_W, numberToWords(totals.receiptTotal), meta.remarks);
  const summaryH = measureSummary(summaryRows);
  const rowH = Math.max(wordsM.height, summaryH);

  y = ensureSpace(doc, y, rowH, onNewPage);
  drawWordsCard(doc, SIDE_M, y, CARD_W, rowH, 'Amount in Words & QC Notes',
    wordsM.wordLines, wordsM.remarkLines);
  drawSummaryCard(doc, CARD_X_RIGHT, y, CARD_W, rowH, 'Summary',
    summaryRows, 'Final Receipt Amount', Rs(totals.receiptTotal));
  y += rowH;

  // ── 6. SIGNATURE + FOOTER ──────────────────────────────────────────────────
  y = ensureSpace(doc, y, 18, onNewPage);
  drawSignatureRow(
    doc,
    y,
    `Received by  ·  ${displayOrDash(storeName)}`,
    'Authorised Signatory',
  );

  stampFooters(doc, identity, letterhead);
  return doc;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function getGrnPdfFilename(grn) {
  return `${grn?.grn_number || 'GRN'}.pdf`;
}

export function buildGrnPdfBlob(data) {
  return createGrnPdfDocument(data).output('blob');
}

export function openGrnPdfUrl(url) {
  window.open(`${url}#zoom=page-fit`, '_blank');
}

export function downloadGrnPdfUrl(url, filename) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
}

export async function fetchGrnPdfData(grnId, scope = {}, extras = {}) {
  const [grn, itemsRaw, catalog, uomsRaw] = await Promise.all([
    getGrn(grnId),
    getGrnItems(grnId, scope).catch(() => []),
    fetchItemCatalog().catch(() => null),
    getUoms().catch(() => []),
  ]);

  const uomMap = {};
  normalizeItemList(uomsRaw).forEach(u => {
    const id = u.uom_id || u.id;
    if (id) uomMap[id] = u.uom_name || u.uom_code || u.name || '';
  });

  const items = enrichGrnItems(normalizeItemList(itemsRaw), catalog, uomMap);

  let vendor = extras.vendor || null;
  if (!vendor && grn.vendor_id) {
    try { vendor = await getVendor(grn.vendor_id); } catch { /* non-critical */ }
  }

  return {
    grn,
    items,
    vendor,
    extras,
    facilityLogo: extras.facilityLogo || null,
    letterhead: extras.letterhead,
  };
}

export async function buildGrnPdfPackage(grnId, scope = {}, extras = {}) {
  const data = await fetchGrnPdfData(grnId, scope, extras);
  const blob = buildGrnPdfBlob(data);
  const url  = URL.createObjectURL(blob);
  return { data, blob, url, filename: getGrnPdfFilename(data.grn) };
}

export async function downloadGrnPdf(grnId, scope = {}, extras = {}) {
  const data = await fetchGrnPdfData(grnId, scope, extras);
  const doc  = createGrnPdfDocument(data);
  doc.save(getGrnPdfFilename(data.grn));
}
